import moment from "moment";
import Teachr from "../../Objects/models/Teachr";
import { validateDeposit, validatePrice } from "../../Utils/Function";
import { isDepositEnabled } from "../../config/environment.config";
import { MAX_COURSES_PER_MONTH, MonthlyQuota, monthlyQuotaOf } from "../../Utils/monthlyQuota";
import { VT_MONTHLY_CEILING_TTC, isVerifiedTrainer, vtTtcFromHourlyPaid } from "../../Utils/VerifiedTrainerAmounts";
import { sessionDescriptionOverrideError } from "../../Utils/sessionDescriptionOverride";
import {
    MIN_SESSIONS_MONTH,
    SessionRow,
    apiSessionsToRows,
    sessionEnd,
    sessionStart,
    sessionsTotalHours,
    sortSessionRows,
    validateSessionRows,
} from "../DeclarationCourse/SessionList";
import { RowIssue, rowIssues } from "../DeclarationV2/components/planningRules";
import { computeAmounts } from "../DeclarationV2/components/amounts";
import { isRelationTeachrCourse, relationCeiling, relationHourlyRate, relationRemuneration } from "../DeclarationV2/components/relationTeachr";
import { emptyDeposit } from "../DeclarationV2/types";
import { DepositErrors } from "../DeclarationV2/components/DepositPanel";
import { clock, dayLabel, hours as formatHours } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { isUndetailedMonthCourse } from "../CoursesV2/courseRows";
import { timeValue } from "../DeclarationV2/components/planningRules";
import { Draft, DraftGroup, DraftStudent } from "./types";

/**
 * Helpers purs des brouillons v2 : lecture de la réponse de l'API (groupes
 * par élève), décalage d'un mois, montants et contrôles. Tout est copié de
 * `CourseRowActions.tsx` (brouillon : `isCoursePass = true`) ; testé dans
 * `src/Test/BrouillonsV2/drafts.test.ts`.
 */

/** Décalage d'un brouillon : le mois précédent est rejoué sur le mois en cours. */
export const DRAFT_MONTH_OFFSET = 1;

const toNumber = (value: unknown): number => {
    const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
    return Number.isFinite(n) ? n : 0;
};

/** Prix horaire payé par le client : `teachrPrice / (1 − frais) / 2`, comme le formulaire existant. */
export const hourlyPaidPrice = (teachrPrice: unknown, defaultFees: unknown): string => {
    const fees = toNumber(defaultFees);
    const price = toNumber(teachrPrice) / (1 - fees) / 2;
    return Number.isFinite(price) && price > 0 ? price.toFixed(2) : "";
};

/** Élève d'un groupe : l'enfant quand il est renseigné, sinon le parent (client). */
export const studentFromGroup = (group: any): DraftStudent => ({
    clientId: toNumber(group?.clientId),
    childId: toNumber(group?.forChild_id),
    firstName: group?.forChildFirstName || group?.firstName || "",
    lastName: group?.forChildLastName || group?.lastName || "",
    addressId: null,
    activationDate: group?.startAt?.date ?? group?.startAt ?? null,
    zip: group?.zip ?? null,
});

/**
 * Séances d'un brouillon : pour un cours du mois, les séances de l'API
 * décalées d'un mois avec le même jour de semaine (`apiSessionsToRows`) ; pour
 * un cours ponctuel, une seule ligne un mois plus tard, même heure et durée.
 */
export const draftSessions = (course: any): SessionRow[] => {
    // Ancien cours sans détail des séances : sa seule séance est une reprise
    // placée au premier jour avec toute la durée, on ne la recopie pas.
    if (isUndetailedMonthCourse(course)) return [];
    if (course?.isPunctualMonth) {
        return apiSessionsToRows(course?.sessions, DRAFT_MONTH_OFFSET);
    }
    const start = moment(course?.startDatetime).add(DRAFT_MONTH_OFFSET, "month");
    return [{
        key: `single-${course?.id ?? "draft"}`,
        date: start.format("YYYY-MM-DD"),
        startTime: start.toISOString(),
        duration: toNumber(course?.duration) / 2,
    }];
};

/** Un cours de l'API devient un brouillon normalisé. */
export const draftFromCourse = (course: any, group: any, teachr: Teachr | null | undefined): Draft => {
    const student = studentFromGroup(group);
    const apiSessions = Array.isArray(course?.sessions) ? course.sessions.length : 0;
    const hasDeposit = isDepositEnabled() && toNumber(course?.deposit) > 0;
    return {
        id: toNumber(course?.id),
        student: { ...student, addressId: course?.address_id ?? null },
        subjectId: course?.subjectAsked_id ?? null,
        subjectLabel: course?.subjectAsked ?? "",
        multiple: !!course?.isPunctualMonth,
        sessions: draftSessions(course),
        needsDates: isUndetailedMonthCourse(course),
        previousStartTime: timeValue(course?.startDatetime),
        price: isRelationTeachrCourse(course) ? relationHourlyRate(course).toFixed(2) : hourlyPaidPrice(course?.teachrPrice, (teachr as any)?.defaultFees),
        // brouillon : l'acompte est à certifier de nouveau (attestation et preuve), la date proposée est aujourd'hui
        deposit: hasDeposit
            ? { ...emptyDeposit(), enabled: true, amount: String(toNumber(course?.deposit)), date: moment().format("YYYY-MM-DD") }
            : emptyDeposit(),
        sessionDescriptionOverride: course?.sessionDescriptionOverride ?? null,
        isTeachrContact: !!course?.customFees?.is_teachr_contact,
        isRelationTeachr: isRelationTeachrCourse(course),
        coursesThisRollingMonth: course?.coursesThisRollingMonth ?? null,
        coursesMonthByThisTeachr: course?.coursesMonthByThisTeachr ?? null,
        amountDeclaredThisMonth: toNumber(course?.amount),
        minSessions: course?.isPunctualMonth && apiSessions < MIN_SESSIONS_MONTH && !isUndetailedMonthCourse(course) ? Math.max(1, apiSessions) : MIN_SESSIONS_MONTH,
        raw: course,
    };
};

/** Réponse de l'API (`{ courses: [{ clientId, forChild_id, courses: [...] }] }`) en groupes de brouillons. */
export const draftGroupsFromApi = (payload: any, teachr: Teachr | null | undefined): DraftGroup[] => {
    const groups: any[] = Array.isArray(payload?.courses) ? payload.courses : [];
    return groups
        .map((group, index) => ({
            key: String(group?.forChild_id ?? group?.clientId ?? index),
            student: studentFromGroup(group),
            drafts: (Array.isArray(group?.courses) ? group.courses : []).map((course: any) => draftFromCourse(course, group, teachr)),
        }))
        .filter((group) => group.drafts.length > 0);
};

/** Retrouve un brouillon par identifiant de cours source. */
export const findDraft = (groups: DraftGroup[], id: number): Draft | null =>
    groups.flatMap((g) => g.drafts).find((d) => d.id === id) ?? null;

export const draftHours = (draft: Draft): number => sessionsTotalHours(draft.sessions);

/** « Vous recevez » : mêmes calculs que l'étape Tarif du nouveau parcours (relation Teach'r : tarif × heures). */
export const draftReceived = (draft: Draft, teachr: Teachr | null | undefined): number =>
    draft.isRelationTeachr
        ? relationRemuneration(draft.raw, draftHours(draft)).total
        : computeAmounts(draft.price, draftHours(draft), teachr, { zip: draft.student.zip }, 0).received;

/** Période du brouillon : première et dernière séance datées. */
export const draftPeriod = (draft: Draft): { start: moment.Moment | null; end: moment.Moment | null } => {
    const starts = draft.sessions.map(sessionStart).filter(Boolean) as moment.Moment[];
    const ends = draft.sessions.map(sessionEnd).filter(Boolean) as moment.Moment[];
    return { start: starts.length ? moment.min(starts) : null, end: ends.length ? moment.max(ends) : null };
};

/** « Mercredi 28 octobre » ou « Vendredi 16 et lundi 26 octobre » (maquette 1g). */
export const draftDatesLabel = (draft: Draft): string => {
    const dates = sortSessionRows(draft.sessions).map((row) => row.date).filter((d): d is string => !!d);
    if (dates.length === 0) return draft.needsDates ? "Jours à choisir" : "Dates à choisir";
    if (dates.length === 1) return dayLabel(dates[0]);
    const sameMonth = dates.every((d) => d.slice(0, 7) === dates[0].slice(0, 7));
    const short = (d: string, withMonth: boolean) => {
        const m = moment(d).locale("fr");
        return withMonth ? `${m.format("dddd D MMMM")}` : `${m.format("dddd D")}`;
    };
    if (dates.length === 2) {
        return `${short(dates[0], !sameMonth)} et ${short(dates[1], true)}`.replace(/^./, (c) => c.toUpperCase());
    }
    const head = dates.slice(0, -1).map((d) => short(d, !sameMonth)).join(", ");
    return `${head} et ${short(dates[dates.length - 1], true)}`.replace(/^./, (c) => c.toUpperCase());
};

/** « 18h00 à 19h30 » pour un cours, « 12h00, 2 séances » pour plusieurs (maquette 1g). */
export const draftScheduleLabel = (draft: Draft): string => {
    const rows = sortSessionRows(draft.sessions);
    const first = rows.find((row) => sessionStart(row));
    if (!first) return draft.needsDates ? "Jours à choisir" : "Horaire à choisir";
    const start = sessionStart(first)!;
    if (!draft.multiple) return `${clock(start)} à ${clock(sessionEnd(first)!)}`;
    return `${clock(start)}, ${rows.length} séance${rows.length > 1 ? "s" : ""}, ${formatHours(draftHours(draft))}`;
};

export interface DraftCeiling {
    declared: number;
    thisDeclaration: number;
    total: number;
    ceiling: number;
    exceeds: boolean;
}

/**
 * Plafond mensuel, copie de `CourseRowActions` : déjà déclaré ce mois
 * (`cours.amount`, net hors Verified Trainer) plus cette déclaration, comparé
 * au centime à 1 980 € × (1 − frais), ou 1 980 € TTC pour Verified Trainer.
 * Ne s'applique que si la première séance est dans le mois en cours.
 */
export const draftCeiling = (draft: Draft, teachr: Teachr | null | undefined, now: moment.Moment = moment()): DraftCeiling => {
    if (draft.isRelationTeachr) {
        // relation Teach'r : ce que paie le client (tarif du prof + part de la plateforme), jamais affiché
        const { start } = draftPeriod(draft);
        const currentMonth = !!start && start.format("YYYY-MM") === now.format("YYYY-MM");
        return relationCeiling(draft.raw, draftHours(draft), draft.amountDeclaredThisMonth, currentMonth);
    }
    const isVt = isVerifiedTrainer();
    const netMultiplier = 1 - (toNumber((teachr as any)?.defaultFees) || 0);
    const hours = draftHours(draft);
    const price = toNumber(draft.price);
    const declared = isVt ? draft.amountDeclaredThisMonth : draft.amountDeclaredThisMonth * netMultiplier;
    const thisDeclaration = isVt ? vtTtcFromHourlyPaid(price, hours) : hours * price * 2 * netMultiplier;
    const ceiling = isVt ? VT_MONTHLY_CEILING_TTC : 1980 * netMultiplier;
    const total = declared + thisDeclaration;
    const { start } = draftPeriod(draft);
    const currentMonth = !!start && start.format("YYYY-MM") === now.format("YYYY-MM");
    const exceeds = currentMonth && price > 0 && thisDeclaration > 0 && Math.round(total * 100) / 100 > Math.round(ceiling * 100) / 100;
    return { declared, thisDeclaration, total, ceiling, exceeds };
};

export const draftQuota = (draft: Draft): MonthlyQuota | null =>
    monthlyQuotaOf(draft.coursesThisRollingMonth, draft.coursesMonthByThisTeachr);

export interface DraftIssues {
    /** Erreur par séance (clé de ligne), à afficher sur la carte. */
    rows: RowIssue[];
    /** Erreur globale sur les séances (nombre minimum, mois différents). */
    sessions: string | null;
    price: string | null;
    deposit: DepositErrors;
    description: string | null;
    quota: string | null;
    /** Toutes les erreurs, dans l'ordre d'affichage, pour le résumé près des boutons. */
    all: string[];
}

export const hasDraftIssues = (issues: DraftIssues): boolean => issues.all.length > 0;

/**
 * Contrôles d'un brouillon avant déclaration, copiés de `validatePunctual(true)`
 * et exprimés par champ : séances (règles du planning v2 : fin de journée,
 * mois en cours, chevauchements entre séances, nombre minimum), prix (6 à
 * 138 € de l'heure), plafond mensuel, acompte (montant au plus égal à ce que
 * paie le client, date, attestation, preuve), description propre au cours,
 * quota de 8 cours. Les chevauchements avec d'autres cours du prof sont
 * vérifiés à part (`fetchExternalOverlap`), au moment de déclarer.
 */
export const draftIssues = (draft: Draft, teachr: Teachr | null | undefined, now: moment.Moment = moment()): DraftIssues => {
    const hours = draftHours(draft);
    const rows = rowIssues(draft.sessions, { activationDate: draft.student.activationDate, existing: [], referenceMonth: now.format("YYYY-MM") });
    // Comme l'existant : le nombre de séances et le mois commun ne concernent qu'un cours du mois ;
    // un seul cours n'a qu'une ligne, contrôlée par rowIssues (jour, heure, durée, fin de journée).
    const sessionsCheck = draft.multiple ? validateSessionRows(draft.sessions, { minSessions: draft.minSessions }) : { isValid: true, message: "" };
    const sessions = rows.length === 0 && !sessionsCheck.isValid ? sessionsCheck.message : null;

    const priceCheck = validatePrice(draft.price, hours);
    let price: string | null = priceCheck.isValid ? null : priceCheck.message;
    const ceiling = draftCeiling(draft, teachr, now);
    if (!price && ceiling.exceeds) {
        price = draft.isRelationTeachr
            ? "Plafond mensuel de l'avance immédiate dépassé pour cet élève. Retirez des séances ou déplacez-les sur le mois suivant."
            : `Total déclaré ce mois (${ceiling.total.toFixed(0)} €) au-dessus du plafond de ${ceiling.ceiling.toFixed(0)} €. Baissez le prix ou déplacez des séances sur le mois suivant.`;
    }

    const deposit: DepositErrors = {};
    if (draft.deposit.enabled && toNumber(draft.deposit.amount) > 0) {
        // comme le formulaire existant : l'acompte ne dépasse pas ce que paie le client (heures × prix horaire payé)
        const paidByClient = hours * toNumber(draft.price);
        const depositCheck = validateDeposit(toNumber(draft.deposit.amount), paidByClient, true);
        if (!depositCheck.isValid) deposit.amount = depositCheck.message;
        if (!draft.deposit.date) deposit.date = "Indiquez la date de réception de l'acompte.";
        else if (draft.student.activationDate && moment(draft.deposit.date).format("YYYY-MM-DD") < moment(draft.student.activationDate).format("YYYY-MM-DD")) {
            deposit.date = `La date d'acompte ne peut pas être antérieure à la validation du compte de ${vocab.yourClient()}.`;
        }
        if (!draft.deposit.attestationAccepted) deposit.attestation = "Vous avez opté pour la déclaration avec acompte, certifiez l'attestation sur l'honneur pour continuer.";
        if (!draft.deposit.proof) deposit.proof = "Joignez une preuve d'acompte (PDF, PNG ou JPEG) pour continuer.";
    }

    const descriptionError = sessionDescriptionOverrideError(draft.sessionDescriptionOverride);
    const description = descriptionError === "" ? null : descriptionError;

    const quotaInfo = draftQuota(draft);
    const { start } = draftPeriod(draft);
    const currentMonth = !!start && start.format("YYYY-MM") === now.format("YYYY-MM");
    const quota = currentMonth && quotaInfo?.reached
        ? `Quota de ${MAX_COURSES_PER_MONTH} ${vocab.courses()} atteint pour ${draft.student.firstName} ce mois : déplacez ${vocab.thisCourse()} sur le mois suivant.`
        : null;

    const all = [
        ...rows.map((issue) => {
            const row = draft.sessions.find((r) => r.key === issue.key);
            const label = row?.date ? dayLabel(row.date).toLowerCase() : `la séance ${draft.sessions.findIndex((r) => r.key === issue.key) + 1}`;
            return `${label.charAt(0).toUpperCase()}${label.slice(1)} : ${issue.message}`;
        }),
        sessions,
        price,
        deposit.amount,
        deposit.date,
        deposit.attestation,
        deposit.proof,
        description,
        quota,
    ].filter((m): m is string => !!m);

    return { rows, sessions, price, deposit, description, quota, all };
};

/** « Une séance est en conflit. Corrigez le lundi 26 octobre pour pouvoir déclarer. » (maquette 1h) */
export const draftIssuesSummary = (draft: Draft, issues: DraftIssues): string | null => {
    if (issues.all.length === 0) return null;
    const firstRow = issues.rows[0];
    if (firstRow) {
        const row = draft.sessions.find((r) => r.key === firstRow.key);
        const label = row?.date ? dayLabel(row.date).toLowerCase() : `la séance ${draft.sessions.findIndex((r) => r.key === firstRow.key) + 1}`;
        return issues.rows.length > 1
            ? `${issues.rows.length} séances sont à corriger. Commencez par ${label} pour pouvoir déclarer.`
            : `Une séance est à corriger. Corrigez ${label} pour pouvoir déclarer.`;
    }
    return issues.all.length > 1 ? `${issues.all.length} points sont à corriger pour pouvoir déclarer.` : issues.all[0];
};
